import React from 'react';
import PropTypes from 'prop-types';
import {
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
  Checkbox,
  FormGroup,
  Typography,
  Box,
  Chip,
} from '@mui/material';
import { PageType, SelectorKind, PriorityEntity } from '../../../catalogs/pageCatalog.generated';
import DescribedField from '../DescribedField';

// Catalogados = int (modelo v3). Os values dos seletores são os ints do catálogo.
const SELECTOR_KIND_OPTIONS = [
  { value: SelectorKind.Dropdown, label: 'Dropdown' },
  { value: SelectorKind.Radio, label: 'Radio (botões visíveis)' },
  { value: SelectorKind.Cards, label: 'Cards visuais' },
  { value: SelectorKind.List, label: 'Lista compacta' },
];

const PRIORITY_LEVELS = [
  { value: PriorityEntity.Place, label: 'Local' },
  { value: PriorityEntity.Professional, label: 'Profissional' },
  { value: PriorityEntity.Service, label: 'Serviço' },
];

// Ordem padrão de escolha (int do catálogo PriorityEntity): serviço → profissional → local.
const DEFAULT_PRIORITY_ORDER = [PriorityEntity.Service, PriorityEntity.Professional, PriorityEntity.Place];

/**
 * SchedulesBlockEditor — Editor do bloco Agendas / Booking
 * TODO 4.7
 *
 * Props:
 *   - value: { scheduleIds, selectorKind, priorityOrder, anyProfessional, ctaLabel, showServiceDescription }
 *   - onChange: (patch) => void
 *   - context: { siteType, availableSchedules } — siteType é PageType enum int (1=LinkInBio, 2=Professional, 3=Company)
 */
const SchedulesBlockEditor = ({ value = {}, onChange, context = {} }) => {
  const {
    scheduleIds = [],
    anyProfessional = false,
    ctaLabel = 'Agendar',
    showServiceDescription = false,
  } = value;

  // selectorKind/priorityOrder são int (v3). Default quando o config não traz o campo
  // (bloco novo criado com config {}). `?? ` preserva o int 0 (Dropdown).
  const selectorKind = value.selectorKind ?? SelectorKind.Dropdown;
  const priorityOrder = (Array.isArray(value.priorityOrder) && value.priorityOrder.length)
    ? value.priorityOrder
    : DEFAULT_PRIORITY_ORDER;

  const { availableSchedules = [], siteType = '' } = context;

  // Patch reescreve selectorKind/priorityOrder (com defaults) — garante que um bloco novo
  // (config {}) persista os ints ao editar qualquer campo.
  const patch = (field, val) => onChange({ ...value, selectorKind, priorityOrder, [field]: val });

  const toggleSchedule = (id) => {
    const updated = scheduleIds.includes(id)
      ? scheduleIds.filter((s) => s !== id)
      : [...scheduleIds, id];
    patch('scheduleIds', updated);
  };

  const movePriority = (index, direction) => {
    const arr = [...priorityOrder];
    const target = index + direction;
    if (target < 0 || target >= arr.length) return;
    [arr[index], arr[target]] = [arr[target], arr[index]];
    patch('priorityOrder', arr);
  };

  return (
    <>
      <DescribedField
        label="Agendas exibidas"
        description="Quais das suas agendas aparecem neste site. São as agendas cadastradas em Agendas. Para criar ou editar agendas, vá para a seção Agendas. Somente agendas selecionadas aqui ficam disponíveis para o visitante."
        helperLink={{ entity: 'schedule' }}
      >
        {availableSchedules.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nenhuma agenda disponível. Crie agendas em Agendas primeiro.
          </Typography>
        ) : (
          <FormGroup>
            {availableSchedules.map((s) => (
              <FormControlLabel
                key={s.id}
                control={
                  <Checkbox
                    checked={scheduleIds.includes(s.id)}
                    onChange={() => toggleSchedule(s.id)}
                  />
                }
                label={s.name || s.id}
              />
            ))}
          </FormGroup>
        )}
      </DescribedField>

      <DescribedField
        label="Tipo de seletor"
        description="Como o visitante escolhe entre serviços, profissionais e locais. 'Dropdown' ocupa pouco espaço e funciona bem com muitas opções. 'Radio' deixa tudo visível (até ~5 opções). 'Cards' é mais visual, ideal para serviços com descrição e preço. 'Lista' é compacto e formal. Se um nível tiver apenas 1 opção, ele aparece automaticamente como rótulo, sem seletor."
      >
        <TextField
          select
          fullWidth
          value={selectorKind}
          onChange={(e) => patch('selectorKind', e.target.value)}
          size="small"
        >
          {SELECTOR_KIND_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
      </DescribedField>

      <DescribedField
        label="Ordem de escolha"
        description="A ordem em que o visitante decide. Ex.: 'Serviço → Profissional → Local' pergunta primeiro o serviço, depois o profissional. Em páginas profissionais (um só profissional), o nível 'Profissional' é omitido automaticamente. Use as setas para reordenar."
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {priorityOrder.map((level, i) => {
            const option = PRIORITY_LEVELS.find((l) => l.value === level);
            return (
              <Box
                key={level}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  px: 2,
                  py: 1,
                }}
              >
                <Typography sx={{ flex: 1 }}>{i + 1}. {option?.label ?? level}</Typography>
                <Chip
                  label="↑"
                  size="small"
                  onClick={() => movePriority(i, -1)}
                  disabled={i === 0}
                  sx={{ cursor: 'pointer' }}
                />
                <Chip
                  label="↓"
                  size="small"
                  onClick={() => movePriority(i, 1)}
                  disabled={i === priorityOrder.length - 1}
                  sx={{ cursor: 'pointer' }}
                />
              </Box>
            );
          })}
        </Box>
      </DescribedField>

      {siteType === PageType.Company && (
        <DescribedField
          label="Qualquer profissional"
          description="Se ligado, o visitante não vê os nomes dos profissionais. Os horários são agrupados e, ao confirmar, o sistema escolhe aleatoriamente um profissional disponível para aquele slot. Útil para distribuir demanda. Em páginas profissionais, esta opção é ignorada."
        >
          <FormControlLabel
            control={
              <Switch
                checked={anyProfessional}
                onChange={(e) => patch('anyProfessional', e.target.checked)}
              />
            }
            label="Ocultar profissional e sortear automaticamente"
          />
        </DescribedField>
      )}

      <DescribedField
        label="Texto do botão de agendamento"
        description="Texto exibido no botão final que confirma o agendamento. Alternativas comuns: 'Reservar', 'Marcar consulta', 'Agendar horário'. Padrão: 'Agendar'."
      >
        <TextField
          fullWidth
          value={ctaLabel}
          onChange={(e) => patch('ctaLabel', e.target.value)}
          inputProps={{ maxLength: 40 }}
          placeholder="Agendar"
          size="small"
        />
      </DescribedField>

      <DescribedField
        label="Mostrar descrição dos serviços"
        description="Se ligado, a descrição de cada serviço aparece abaixo do nome no seletor. Útil quando os serviços têm diferenças importantes que o visitante precisa entender antes de escolher."
      >
        <FormControlLabel
          control={
            <Switch
              checked={showServiceDescription}
              onChange={(e) => patch('showServiceDescription', e.target.checked)}
            />
          }
          label="Exibir descrição abaixo do nome do serviço"
        />
      </DescribedField>
    </>
  );
};

SchedulesBlockEditor.propTypes = {
  value: PropTypes.shape({
    scheduleIds: PropTypes.arrayOf(PropTypes.string),
    selectorKind: PropTypes.number, // int do catálogo SelectorKind (v3)
    priorityOrder: PropTypes.arrayOf(PropTypes.number), // int[] do catálogo PriorityEntity (v3)
    anyProfessional: PropTypes.bool,
    ctaLabel: PropTypes.string,
    showServiceDescription: PropTypes.bool,
  }),
  onChange: PropTypes.func.isRequired,
  context: PropTypes.shape({
    siteType: PropTypes.string,
    scope: PropTypes.object,
    availableSchedules: PropTypes.array,
  }),
};

export default SchedulesBlockEditor;
